import { StringKeyedDictionary } from '@/typescriptHelpers';
import { getIsApplePlatform } from '../helpers';
import {
    AppEntity,
    AppPlatform,
    ReleaseStepType,
    ReleaseType,
    AppDevice,
    ASCRegistrationStatus,
    DeviceClass,
    AppSettings,
} from '../types';
import { legacyAppSettingsMock } from './appSettings';
import { RecursiveStringifyEnums } from './buildDistro';

const DEMO_USERS: string[] = [
    'ssullenberger@demo.com',
    'aearhart@demo.com',
    'narmstrong@demo.com',
    'mjemison@demo.com',
];

const DEMO_DEVICES: AppDevice[] = [
    {
        id: 'A3R4RN8R7V',
        appStoreId: 'A3R4RN8R7V',
        displayName: "Sully's iPhone 13 Mini",
        udid: '00008110-000E0D4221C2802E',
        ascRegistrationStatus: ASCRegistrationStatus.Enabled,
        isRegisteredInRunway: false,
        isTestDevice: false,
        associatedUserEmail: 'ssullenberger@demo.com',
        deviceClass: DeviceClass.iPhone,
        registeredInASCByAutomationAt: undefined,
        updatedStatusInASCByAutomationAt: undefined,
    },
    {
        id: 'TV49B7ALJX',
        appStoreId: 'TV49B7ALJX',
        displayName: "Amelia's iPhone",
        udid: '00008120-001228413C9B402E',
        ascRegistrationStatus: ASCRegistrationStatus.Enabled,
        isRegisteredInRunway: false,
        isTestDevice: false,
        associatedUserEmail: 'aearhart@demo.com',
        deviceClass: DeviceClass.iPhone,
        registeredInASCByAutomationAt: undefined,
        updatedStatusInASCByAutomationAt: undefined,
    },
];

const RELEASE_STEP_ARCHETYPES: StringKeyedDictionary<ReleaseStepType[]> = {
    [ReleaseType.Major]: [
        ReleaseStepType.Kickoff,
        ReleaseStepType.FeatureReadiness,
        ReleaseStepType.Translations,
        ReleaseStepType.ReleaseCandidate,
        ReleaseStepType.RegressionTesting,
        ReleaseStepType.BetaTesting,
        ReleaseStepType.Screenshots,
        ReleaseStepType.Metadata,
        ReleaseStepType.Approvals,
        ReleaseStepType.Submission,
        ReleaseStepType.StoreReview,
        ReleaseStepType.Takeoff,
        ReleaseStepType.ReleaseCycle,
    ],
    [ReleaseType.Minor]: [
        ReleaseStepType.Kickoff,
        ReleaseStepType.FeatureReadiness,
        ReleaseStepType.Translations,
        ReleaseStepType.ReleaseCandidate,
        ReleaseStepType.RegressionTesting,
        ReleaseStepType.BetaTesting,
        ReleaseStepType.Screenshots,
        ReleaseStepType.Metadata,
        ReleaseStepType.Approvals,
        ReleaseStepType.Submission,
        ReleaseStepType.StoreReview,
        ReleaseStepType.Takeoff,
        ReleaseStepType.ReleaseCycle,
    ],
    [ReleaseType.Point]: [
        ReleaseStepType.Kickoff,
        ReleaseStepType.FeatureReadiness,
        ReleaseStepType.Translations,
        ReleaseStepType.ReleaseCandidate,
        ReleaseStepType.RegressionTesting,
        ReleaseStepType.BetaTesting,
        ReleaseStepType.Screenshots,
        ReleaseStepType.Metadata,
        ReleaseStepType.Approvals,
        ReleaseStepType.Submission,
        ReleaseStepType.StoreReview,
        ReleaseStepType.Takeoff,
        ReleaseStepType.ReleaseCycle,
    ],
    [ReleaseType.Hotfix]: [
        ReleaseStepType.Kickoff,
        ReleaseStepType.FeatureReadiness,
        ReleaseStepType.Translations,
        ReleaseStepType.ReleaseCandidate,
        ReleaseStepType.RegressionTesting,
        ReleaseStepType.BetaTesting,
        ReleaseStepType.Screenshots,
        ReleaseStepType.Metadata,
        ReleaseStepType.Approvals,
        ReleaseStepType.Submission,
        ReleaseStepType.StoreReview,
        ReleaseStepType.Takeoff,
        ReleaseStepType.ReleaseCycle,
    ],
    [ReleaseType.Rollback]: [
        ReleaseStepType.RollbackBuild,
        ReleaseStepType.Screenshots,
        ReleaseStepType.Metadata,
        ReleaseStepType.Approvals,
        ReleaseStepType.Submission,
        ReleaseStepType.StoreReview,
        ReleaseStepType.Takeoff,
        ReleaseStepType.ReleaseCycle,
    ],
};

export const legacyAppMock: (platform: AppPlatform) => RecursiveStringifyEnums<AppEntity> = (platform) => ({
    id: `appollo-${platform}`,
    appId: `appollo-${platform}`,
    appUsesStaticReleaseBranch: false,
    appHome: null,
    workflowId: null,
    isRestrictedApp: false,
    buildBuckets: [],
    createdAt: '2021-06-22T13:19:57.673369514Z',
    releaseStepArchetypes: RELEASE_STEP_ARCHETYPES,
    appSettings: legacyAppSettingsMock(`appollo-${platform}`, 'Appollo', platform) as unknown as AppSettings,
    devices: DEMO_DEVICES,
    provisioningProfiles:
        getIsApplePlatform(platform) ?
            [
                {
                    id: '4WZX8G45T4',
                    name: 'Runway managed: Appollo all devices',
                    profileType: 'IOS_APP_DEVELOPMENT',
                    expirationDate: '2025-04-03T19:00:56Z',
                    creationDate: '2024-04-03T19:00:56Z',
                    profileState: 'ACTIVE',
                    certificates: [
                        {
                            id: 'BHAV2H7496',
                            name: 'Apple Development: Sully Sullenberger',
                            expirationDate: '2025-02-28T17:17:28Z',
                            certificateType: 'DEVELOPMENT',
                        },
                    ],
                    devices: [],
                    bundleId: 'com.demo.demo',
                    bundleAppStoreId: 'TP9CN9T56D',
                    createdByRunwayAt: null,
                },
                {
                    id: 'UA7HPK3GLN',
                    name: 'iOS ad-hoc - (com.demo.demo)',
                    profileType: 'IOS_APP_ADHOC',
                    expirationDate: '2025-02-07T16:55:18Z',
                    creationDate: '2024-02-09T19:16:04Z',
                    profileState: 'ACTIVE',
                    certificates: [
                        {
                            id: 'ZS452QN59Y',
                            name: 'Apple Distribution: Appollo, LLC',
                            expirationDate: '2025-02-07T16:55:18Z',
                            certificateType: 'DISTRIBUTION',
                        },
                    ],
                    devices: [],
                    bundleId: 'com.runway.demo',
                    bundleAppStoreId: '979URDT3QA',
                    createdByRunwayAt: null,
                },
            ]
        :   [],
    users: DEMO_USERS,
    stabilityHistory: null,
    completedReleaseTotalCount: 1,
});

export const mockWorkflowsApp = (appId: string, appSettings: AppSettings): AppEntity => ({
    id: appId,
    appId,
    workflowId: `${appId}-wf`,
    users: DEMO_USERS,
    buildBuckets: [],
    stabilityHistory: undefined,
    createdAt: '2026-02-01T13:19:57.673369514Z',
    isRestrictedApp: false,
    completedReleaseTotalCount: 1, // TODO
    devices: DEMO_DEVICES,
    provisioningProfiles: [], // TODO
    releaseStepArchetypes: RELEASE_STEP_ARCHETYPES,
    appSettings,
});
